import { useMemo, useState } from 'react';
import { useApp } from '@/context/AppContext';
import { StatusBadge, Badge } from '@/components/ui/Badge';

function initialsFor(name) {
  return (name || '').split(/\s+/).filter(Boolean).map(p => p[0]).slice(0, 2).join('').toUpperCase() || '??';
}

export default function Routes() {
  const { todayStops, custById, customers, team, openModal, setActiveStopId, optimizeStops, geocodeAllMissing, generateRouteBrief } = useApp();
  const [optimizing, setOptimizing] = useState(false);
  const [geocoding,  setGeocoding]  = useState(false);
  const [briefing,   setBriefing]   = useState(false);
  const [briefs,     setBriefs]     = useState([]);

  const techNames = useMemo(() => [...new Set(todayStops.map(s => s.tech))], [todayStops]);

  const techInfo = useMemo(() => {
    const map = {};
    team.forEach(t => { map[t.name] = { initials: t.initials || initialsFor(t.name), color: t.color }; });
    return map;
  }, [team]);

  const missingGeo = useMemo(() => {
    const stopCustIds = new Set(todayStops.map(s => s.custId));
    return customers.filter(c => stopCustIds.has(c.id) && (c.lat == null || c.lng == null));
  }, [todayStops, customers]);

  const handleStopClick = (stop) => {
    if (stop.status !== 'done') {
      setActiveStopId(stop.id);
      openModal('stop_detail', { stopId: stop.id });
    }
  };

  const handleOptimize = async () => {
    setOptimizing(true);
    try {
      for (const tech of techNames) {
        const stopIds = todayStops
          .filter(s => s.tech === tech && s.status !== 'done')
          .map(s => s.id);
        if (stopIds.length >= 2) {
          await optimizeStops(stopIds, { returnToOrigin: false });
        }
      }
    } finally {
      setOptimizing(false);
    }
  };

  const handleGeocode = async () => {
    setGeocoding(true);
    try { await geocodeAllMissing(); } finally { setGeocoding(false); }
  };

  const handleBrief = async () => {
    setBriefing(true);
    try {
      const res = await generateRouteBrief();
      if (res.ok) setBriefs(res.briefs || []);
    } finally {
      setBriefing(false);
    }
  };

  const doneCount  = todayStops.filter(s => s.status === 'done').length;
  const totalCount = todayStops.length;

  return (
    <div className="page fade-up">
      <div className="ph">
        <div>
          <div className="ptitle">Routes</div>
          <div className="psub">
            {doneCount} of {totalCount} stops complete
          </div>
        </div>
        <div className="pacts">
          <button className="btn btn-ghost btn-sm" onClick={() => openModal('export', { entity: 'stops' })}>Export</button>
          <button
            className="btn btn-ghost btn-sm"
            onClick={handleBrief}
            disabled={briefing || todayStops.length === 0}
            title="Generate AI morning briefing for each tech"
          >
            {briefing ? 'Briefing…' : '☀ Morning brief'}
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleOptimize}
            disabled={optimizing || todayStops.length < 2 || missingGeo.length > 0}
            title={missingGeo.length > 0 ? 'Geocode customers first' : 'Reorder stops by shortest drive time'}
          >
            {optimizing ? 'Optimizing…' : '✨ AI Optimize'}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => openModal('add_stop')}>
            + Add Stop
          </button>
        </div>
      </div>

      {missingGeo.length > 0 && (
        <div className="card" style={{ marginBottom: 12, borderLeft: '3px solid var(--amber)' }}>
          <div className="card-body" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--t1)' }}>
                {missingGeo.length} customer{missingGeo.length === 1 ? '' : 's'} missing coordinates
              </div>
              <div style={{ fontSize: 11, color: 'var(--t3)', marginTop: 2 }}>
                Geocode them to enable route optimization and arrival alerts.
              </div>
            </div>
            <button className="btn btn-primary btn-sm" onClick={handleGeocode} disabled={geocoding}>
              {geocoding ? 'Geocoding…' : 'Geocode all'}
            </button>
          </div>
        </div>
      )}

      {briefs.length > 0 && (
        <div className="card" style={{ marginBottom: 12, borderLeft: '3px solid var(--cyan)' }}>
          <div className="card-head">
            <span className="card-title">☀ Morning briefing</span>
            <button className="btn btn-ghost btn-sm" onClick={() => setBriefs([])}>Dismiss</button>
          </div>
          <div className="card-body" style={{ display: 'grid', gap: 14 }}>
            {briefs.map((b) => (
              <div key={b.tech} style={{ paddingBottom: 10, borderBottom: '1px solid var(--b1)' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--cyan)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                  {b.tech} · {b.total_stops} stop{b.total_stops === 1 ? '' : 's'}
                </div>
                {b.error ? (
                  <div style={{ fontSize: 12, color: 'var(--rose)' }}>Brief failed: {b.error}</div>
                ) : (
                  <div style={{ fontSize: 13, color: 'var(--t1)', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
                    {b.summary}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="g2">
        {techNames.map(tech => {
          const techStops = todayStops.filter(s => s.tech === tech);
          const done = techStops.filter(s => s.status === 'done').length;
          const info = techInfo[tech] || { initials: tech.slice(0, 2).toUpperCase(), color: 'var(--t2)' };
          const totalTime = techStops.reduce((a, s) => a + (s.duration || 0), 0);

          return (
            <div key={tech} className="card">
              <div className="card-head">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div
                    className="tech-av"
                    style={{
                      width: 28,
                      height: 28,
                      fontSize: 11,
                      background: `linear-gradient(135deg,${info.color},var(--teal))`,
                    }}
                  >
                    {info.initials}
                  </div>
                  <span className="card-title">{tech}</span>
                </div>
                <Badge color="var(--teal)" bg="var(--teal-t)">{done}/{techStops.length} done</Badge>
              </div>

              <div className="card-body-0">
                {techStops.map((stop, i) => {
                  const c = custById(stop.custId);
                  const numStyle = stop.status === 'done' ? 'sn-done' : stop.status === 'active' ? 'sn-active' : 'sn-next';
                  const numLabel = stop.status === 'done' ? '✓' : stop.status === 'active' ? '▶' : i + 1;
                  return (
                    <div
                      key={stop.id}
                      className="stop-row"
                      onClick={() => handleStopClick(stop)}
                    >
                      <div className={`stop-num ${numStyle}`}>{numLabel}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--t1)' }}>{c?.name || ''}</div>
                        <div style={{ fontSize: 11, color: 'var(--t3)' }}>{c?.addr || ''} · {stop.time}</div>
                        {c?.gateCode && (
                          <div style={{ fontSize: 10.5, color: 'var(--t3)', marginTop: 2 }}>
                            Gate: {c.gateCode}
                          </div>
                        )}
                        {c?.chem && stop.status === 'done' && (
                          <div style={{ fontSize: 10.5, color: 'var(--t3)', marginTop: 2 }}>
                            Cl:{c.chem.cl} pH:{c.chem.ph} Alk:{c.chem.alk}
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                        <StatusBadge status={stop.status} />
                        {stop.status === 'pending' && (
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={e => { e.stopPropagation(); handleStopClick(stop); }}
                          >
                            Log
                          </button>
                        )}
                        {stop.photos > 0 && stop.status === 'done' && (
                          <span style={{ fontSize: 10.5, color: 'var(--t3)' }}>
                            {stop.photos} photos
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Footer stats */}
              <div style={{
                padding: '10px 15px',
                borderTop: '1px solid var(--b1)',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                gap: 8,
              }}>
                {[
                  { label: 'Done',   value: done,                    color: 'var(--teal)' },
                  { label: 'Left',   value: techStops.length - done, color: 'var(--cyan)' },
                  { label: 'Logged', value: `${totalTime}m`,         color: 'var(--t1)' },
                ].map(({ label, value, color }) => (
                  <div key={label} style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 15, fontWeight: 800, color }}>{value}</div>
                    <div style={{ fontSize: 10, color: 'var(--t3)', fontWeight: 600, textTransform: 'uppercase' }}>{label}</div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
